import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { stringify } from '../../util/urlHelpers';
import { getDefaultSearchOrigin } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getChildren, computeChildAge, buildChildColorMap } from '../../util/childData';
import { bookingsForView } from '../BookingsPage/BookingsPage.helpers';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import SectionActivities from './sections/SectionActivities/SectionActivities';
import SectionCategories from './sections/SectionCategories/SectionCategories';
import SectionIntro from './sections/SectionIntro/SectionIntro';
import SectionNotifyMe from './sections/SectionNotifyMe/SectionNotifyMe';
import SectionWhyKidHive from './sections/SectionWhyKidHive/SectionWhyKidHive';
import SectionForProviders from './sections/SectionForProviders/SectionForProviders';
import SectionWeekBookings from './sections/SectionWeekBookings/SectionWeekBookings';
import { childAgeParams } from './LandingPage.duck';

import css from './LandingPage.module.css';

/**
 * KidHive homepage (Figma node 0-6784). ONE auth-aware page. Signed out (and during SSR): the public
 * browse homepage -- intro, All activities, Categories, notify-me. Signed in: All activities, Great
 * for {child}, Categories (plus the week-bookings widget when there are upcoming bookings).
 *
 * The activity rows are REAL listings (loadData queries): "All activities" = classes nearest to the
 * parent's saved location, then ONE "Great for {child}" row PER child, each narrowed to that
 * child's age. A row with no results hides. Categories come from the hosted-asset config. Sections
 * are rendered from this container (not the Console PageBuilder asset); PageBuilder stays intact
 * for other pages.
 */
export const LandingPageComponent = props => {
  const {
    currentUser,
    scrollingDisabled,
    isAuthenticated,
    authInfoLoaded,
    allActivityListings = [],
    personalizedListingsByChild = {},
    bookingTransactions = [],
    heroClassCount = null,
  } = props;
  const intl = useIntl();

  const title = intl.formatMessage({ id: 'LandingPage.schemaTitle' });
  const description = intl.formatMessage({ id: 'LandingPage.schemaDescription' });

  const allActivitiesTitle = intl.formatMessage({ id: 'LandingPage.allActivitiesTitle' });

  // Signed-out visitors get the PUBLIC BROWSE homepage (open browse): intro band, the "All
  // activities" row, categories, and the notify-me band. No per-child rows, no week-bookings widget.
  //
  // SSR/HYDRATION RULE: !authInfoLoaded is the SSR pass (the server store never resolves auth), and
  // it must render the SAME tree an anonymous client hydrates into -- or hydration breaks on the
  // mismatch and leaves a blank page (production logout reloads '/' and hit exactly that). So this
  // branch renders ONLY from data the server has too: listings the server's loadData put in the
  // store (the same un-personalized query the anonymous client runs), hosted config and static
  // copy. No currentUser, no distance origin, no window/date reads. Logged-in users never see this
  // HTML stick: authenticated cold loads skip hydration entirely (src/index.js renders clean).
  if (!authInfoLoaded || !isAuthenticated) {
    return (
      <Page
        title={title}
        description={description}
        scrollingDisabled={scrollingDisabled}
        schema={{ '@context': 'http://schema.org', '@type': 'WebPage', name: title, description }}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.rootPublic}>
            <SectionIntro classCount={heroClassCount} />
            <SectionActivities
              title={allActivitiesTitle}
              listings={allActivityListings}
              origin={null}
            />
            {/* Reassurance right after the first classes, as visitors start deciding. */}
            <SectionWhyKidHive />
            {/* Categories live in the hero now (compact two-column pills under the stats). */}
            {/* The invitation to providers: last, so parents see classes and trust points first. */}
            <SectionForProviders />
            <SectionNotifyMe />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const children = getChildren(currentUser).filter(c => c.id && c.firstName);

  // The homepage week view -- the parent's upcoming bookings, marked per child. Only shown when there
  // are any (providers/parents without bookings simply don't see it).
  const childColorMap = buildChildColorMap(children);
  const upcomingBookings = bookingsForView(bookingTransactions, 'upcoming', 'all');

  // Distance chips on the cards.
  const origin = getDefaultSearchOrigin(currentUser);

  // ONE "Great for {child} (age)" row per child, each with that child's listings and a "See all"
  // that carries the child's age filter into the search page. A child with no age-matching classes
  // shows the empty state instead of hiding -- and those rows sort LAST, so children with real
  // classes always lead (stable order within each group).
  const childrenOrdered = [...children].sort(
    (a, b) =>
      ((personalizedListingsByChild[b.id] || []).length > 0 ? 1 : 0) -
      ((personalizedListingsByChild[a.id] || []).length > 0 ? 1 : 0)
  );
  const personalizedSections = childrenOrdered.map(child => {
    const age = computeChildAge(child);
    const sectionTitle = intl.formatMessage(
      { id: 'LandingPage.greatForChild' },
      {
        name: child.firstName,
        showAge: Number.isInteger(age) ? 'yes' : 'no',
        age: Number.isInteger(age) ? age : 0,
      }
    );
    const ageParams = childAgeParams(child);
    return (
      <SectionActivities
        key={child.id}
        title={sectionTitle}
        listings={personalizedListingsByChild[child.id] || []}
        origin={origin}
        seeAllSearch={ageParams ? `?${stringify(ageParams)}` : null}
        emptyMessage={intl.formatMessage(
          { id: 'LandingPage.greatForChildEmpty' },
          {
            name: child.firstName,
            showAge: Number.isInteger(age) ? 'yes' : 'no',
            age: Number.isInteger(age) ? age : 0,
          }
        )}
      />
    );
  });

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={{ '@context': 'http://schema.org', '@type': 'WebPage', name: title, description }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {upcomingBookings.length > 0 ? (
            <SectionWeekBookings
              bookings={upcomingBookings}
              children={children}
              childColorMap={childColorMap}
            />
          ) : null}
          <SectionActivities title={allActivitiesTitle} listings={allActivityListings} origin={origin} />
          {personalizedSections}
          <SectionCategories />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated, authInfoLoaded } = state.auth;
  const {
    allActivityIds,
    personalizedIdsByChild,
    bookingTransactionRefs,
    heroClassCount = null,
  } = state.LandingPage;
  const personalizedListingsByChild = Object.fromEntries(
    Object.entries(personalizedIdsByChild || {}).map(([childId, ids]) => [
      childId,
      getListingsById(state, ids),
    ])
  );
  return {
    currentUser,
    isAuthenticated,
    authInfoLoaded,
    allActivityListings: getListingsById(state, allActivityIds),
    heroClassCount,
    personalizedListingsByChild,
    bookingTransactions: getMarketplaceEntities(state, bookingTransactionRefs || []),
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
